import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { PiCalendarBlankDuotone, PiShareNetworkDuotone, PiWarningCircleDuotone } from 'react-icons/pi';

import Pattern from 'elements/Pattern';

const getImageUrl = (photoKey) => photoKey
    ? `${global.backingsEndpoint}photoRepository/getImageBySize?size=ORIGINAL&key=${photoKey}`
    : null;

export default function LandingPage() {
    const { prettyUrl } = useParams();
    const [searchParams] = useSearchParams();
    const landingPageIdParam = searchParams.get('id');
    const [blog, setBlog] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchBlogDetail = async () => {
            setIsLoading(true);

            try {
                const landingPageId = Number(landingPageIdParam);
                const hasValidId = Number.isInteger(landingPageId) && landingPageId > 0;
                const cleanPrettyUrl = String(prettyUrl || '').trim().replace(/^\/+|\/+$/g, '');

                if (!hasValidId && !cleanPrettyUrl) {
                    setBlog(null);
                    return;
                }

                const method = hasValidId
                    ? 'getLandingPageById'
                    : 'getLandingPageByPrettyUrl';
                const body = hasValidId
                    ? { id: landingPageId }
                    : { prettyUrl: cleanPrettyUrl };
                const url = `${global.backingsEndpoint}landingPageControl/${method}`;
                const response = await axios.post(url, body);
                setBlog(response.data || null);
            } catch (error) {
                console.error('Error fetching blog details:', error);
                setBlog(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchBlogDetail();
    }, [landingPageIdParam, prettyUrl]);

    if (isLoading) return <LandingPageLoading />;
    if (!blog) return <LandingPageNotFound />;

    const mainImageUrl = getImageUrl(blog.mainImage?.photo);

    return (
        <main className={`relative flex w-full flex-1 flex-col items-center overflow-hidden gradient-gray ${blog.className || ''}`}>
            <Pattern className="pointer-events-none opacity-45" />

            <section
                className="relative z-10 flex h-[calc(100svh-7rem)] min-h-[360px] w-full flex-col items-center justify-end overflow-hidden rounded-b-3xl bg-neutral-800 bg-cover bg-center bg-no-repeat px-site py-6 shadow-2xl sm:py-8 lg:py-12"
                style={mainImageUrl ? { backgroundImage: `url(${mainImageUrl})` } : undefined}
            >
                <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/35 to-black/90" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent" />

                <div className="relative z-10 w-full max-w-boxed">
                    <BlogHeader blog={blog} />
                </div>
            </section>

            <div className="relative z-10 flex w-full flex-col gap-6 pt-4 sm:gap-8 sm:pt-4 lg:gap-10 lg:pt-4">
                {(blog.lead || blog.content1) && (
                    <ContentSection featured={Boolean(blog.lead)}>
                        {blog.lead && <RichContent html={blog.lead} className="landing-rich-lead" />}
                        {blog.content1 && <RichContent html={blog.content1} />}
                    </ContentSection>
                )}

                {blog.image1?.photo && <FeatureImage photoKey={blog.image1.photo} alt={blog.hl1 || blog.htmlTitle} />}

                {blog.content2 && (
                    <ContentSection>
                        <RichContent html={blog.content2} />
                    </ContentSection>
                )}

                {blog.image2?.photo && <FeatureImage photoKey={blog.image2.photo} alt={blog.hl1 || blog.htmlTitle} />}

                {blog.content3 && (
                    <ContentSection>
                        <RichContent html={blog.content3} />
                    </ContentSection>
                )}
            </div>
        </main>
    );
}

function ContentSection({ children, featured = false }) {
    return (
        <article className={`landing-rich-content relative w-full overflow-hidden border-y bg-white rounded-3xl px-site py-6 text-neutral-700 shadow-2xl sm:py-8 lg:py-10 ${featured ? 'border-green-100' : 'border-neutral-100'}`}>
            <Pattern className="pointer-events-none opacity-10 mix-blend-multiply" />
            <div className="landing-rich-content-inner relative z-10 mx-auto flex w-full max-w-boxed flex-col gap-5">
                {children}
            </div>
        </article>
    );
}

function RichContent({ html, className = '' }) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

function FeatureImage({ photoKey, alt }) {
    return (
        <div className="w-full px-site">
            <figure className="mx-auto h-[clamp(260px,52vw,680px)] w-full max-w-boxed overflow-hidden rounded-3xl bg-neutral-200 shadow-2xl">
                <img
                    src={getImageUrl(photoKey)}
                    alt={stripHtml(alt) || 'Fürge Diák'}
                    className="size-full object-cover"
                    loading="lazy"
                />
            </figure>
        </div>
    );
}

function BlogHeader({ blog }) {
    const handleShare = async () => {
        const shareData = {
            url: window.location.href,
            title: blog.htmlTitle || stripHtml(blog.hl1) || 'Fürge Diák',
            text: stripHtml(blog.lead) || 'Érdekes tartalom a Fürge Diáktól.',
        };

        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (error) {
                if (error?.name !== 'AbortError') console.error('Error sharing:', error);
            }
            return;
        }

        try {
            await navigator.clipboard.writeText(window.location.href);
        } catch (error) {
            console.error('Copying the link failed:', error);
        }
    };

    const publicationDate = blog.publicationTime
        ? format(new Date(blog.publicationTime), 'yyyy. MMMM d., EEEE', { locale: hu })
        : null;

    return (
        <header className="flex w-full flex-col gap-5 text-left text-white sm:gap-6">
            <h1
                className="max-w-5xl break-words font-display text-3xl font-black leading-[1.05] drop-shadow-lg sm:text-5xl lg:text-6xl xl:text-7xl"
                dangerouslySetInnerHTML={{ __html: blog.hl1 || blog.htmlTitle || '' }}
            />

            <div className="flex flex-col gap-4 border-t border-white/20 pt-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                    <p className="font-display text-base font-bold text-white sm:text-lg">{blog.authorName || 'Fürge Diák'}</p>
                    {publicationDate && (
                        <p className="mt-1 flex items-center gap-2 text-xs text-white/70 sm:text-sm">
                            <PiCalendarBlankDuotone className="size-4 shrink-0" />
                            <span>{publicationDate}</span>
                        </p>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleShare}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 font-display text-sm font-black text-neutral-900 shadow-xl transition hover:-translate-y-0.5 hover:bg-green-50 sm:w-auto"
                >
                    <PiShareNetworkDuotone className="size-5 text-green-600" />
                    Megosztás
                </button>
            </div>
        </header>
    );
}

function LandingPageLoading() {
    return (
        <div className="relative flex min-h-[520px] w-full flex-1 items-center justify-center overflow-hidden gradient-gray">
            <Pattern className="pointer-events-none opacity-45" />
            <div className="relative z-10 flex gap-2" aria-label="Tartalom betöltése">
                <span className="size-4 animate-bounce rounded-full gradient-red" />
                <span className="size-4 animate-bounce rounded-full gradient-green [animation-delay:150ms]" />
                <span className="size-4 animate-bounce rounded-full gradient-blue [animation-delay:300ms]" />
            </div>
        </div>
    );
}

function LandingPageNotFound() {
    return (
        <div className="relative flex min-h-[520px] w-full flex-1 items-center justify-center overflow-hidden gradient-gray px-site py-site">
            <Pattern className="pointer-events-none opacity-45" />
            <div className="relative z-10 flex max-w-lg flex-col items-center rounded-3xl bg-white p-8 text-center text-neutral-800 shadow-2xl">
                <PiWarningCircleDuotone className="mb-4 size-14 text-orange-500" />
                <h1 className="font-display text-2xl font-black sm:text-3xl">A tartalom nem található</h1>
                <p className="mt-2 text-sm text-neutral-500 sm:text-base">Elképzelhető, hogy az oldal már nem érhető el, vagy megváltozott a címe.</p>
            </div>
        </div>
    );
}

function stripHtml(value) {
    return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
